import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Typography, 
  Container,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { currencyService, popularCurrencies } from '../services/api';

const RatesPage = () => {
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [selectedPair, setSelectedPair] = useState('USD/EUR');
  const theme = useTheme();

  // Load exchange rates
  useEffect(() => {
    const loadRates = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await currencyService.getExchangeRates(baseCurrency);
        
        if (response.success) {
          // Convert rates object to array and enrich with currency info
          const ratesArray = Object.entries(response.rates).map(([code, rate]) => {
            const currencyInfo = popularCurrencies.find(c => c.code === code);
            return {
              code,
              rate,
              name: currencyInfo?.name || code,
              flag: currencyInfo?.flag || '',
              change: (Math.random() - 0.5) * 2, // Mock change percentage
            };
          });
          
          // Sort by popularity and then alphabetically
          ratesArray.sort((a, b) => {
            const aPopular = popularCurrencies.some(c => c.code === a.code);
            const bPopular = popularCurrencies.some(c => c.code === b.code);
            
            if (aPopular && !bPopular) return -1;
            if (!aPopular && bPopular) return 1;
            return a.code.localeCompare(b.code);
          });
          
          setRates(ratesArray);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadRates();
  }, [baseCurrency]);

  // Load historical data for selected pair
  useEffect(() => {
    const loadHistoricalData = async () => {
      try {
        const [from, to] = selectedPair.split('/');
        const response = await currencyService.getHistoricalData(from, to, 30);
        
        if (response.success) {
          setHistoricalData(response.historicalData);
        }
      } catch (error) {
        console.error('Error loading historical data:', error);
      }
    };
    
    loadHistoricalData();
  }, [selectedPair]);

  const formatRate = (rate) => {
    if (rate >= 1) {
      return rate.toFixed(4);
    } else {
      return rate.toFixed(6);
    }
  };

  const formatChange = (change) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  const getChangeColor = (change) => {
    return change >= 0 ? theme.palette.success.main : theme.palette.error.main;
  };

  const getChangeIcon = (change) => {
    return change >= 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />;
  };

  const getPopularPairs = () => {
    const pairs = [];
    popularCurrencies.slice(0, 6).forEach(currency => {
      if (currency.code !== baseCurrency) {
        pairs.push(`${baseCurrency}/${currency.code}`);
      }
    });
    return pairs;
  };

  return (
    <>
      <Helmet>
        <title>Exchange Rates - RatePly</title>
        <meta name="description" content="Live exchange rates for 170+ currencies. Track currency trends and historical data." />
      </Helmet>
      
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              mb: 2,
            }}
          >
            Live Exchange Rates
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Track real-time currency exchange rates and historical trends for all major currencies.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Controls */}
          <Grid item xs={12}>
            <Card
              elevation={4}
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Base Currency"
                      value={baseCurrency}
                      onChange={(e) => setBaseCurrency(e.target.value)}
                    >
                      {popularCurrencies.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography component="span">
                              {currency.flag} {currency.code}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {currency.name}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Chart Pair"
                      value={selectedPair}
                      onChange={(e) => setSelectedPair(e.target.value)}
                    >
                      {getPopularPairs().map((pair) => (
                        <MenuItem key={pair} value={pair}>
                          {pair}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<TrendingUpIcon />} 
                        label="Live Rates" 
                        color="primary" 
                        variant="filled"
                      />
                      <Chip 
                        label="Updated Hourly" 
                        color="secondary" 
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Historical Chart */}
          <Grid item xs={12} lg={8}>
            <Card
              elevation={4}
              sx={{
                height: 400,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent sx={{ height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {selectedPair} - 30 Day Trend
                </Typography>
                <Box sx={{ height: 300, mt: 2 }}>
                  {historicalData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={historicalData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(value) => new Date(value).toLocaleDateString()}
                        />
                        <YAxis 
                          domain={['dataMin - 0.01', 'dataMax + 0.01']}
                          tickFormatter={(value) => value.toFixed(4)}
                        />
                        <Tooltip
                          labelFormatter={(value) => new Date(value).toLocaleDateString()}
                          formatter={(value) => [value.toFixed(6), 'Rate']}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="rate" 
                          stroke={theme.palette.primary.main}
                          strokeWidth={3}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Popular Rates Summary */}
          <Grid item xs={12} lg={4}>
            <Card
              elevation={4}
              sx={{
                height: 400,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Popular Rates
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    rates.slice(0, 8).map((rate) => (
                      <Box
                        key={rate.code}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1.5,
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {rate.flag} {rate.code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {rate.name}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatRate(rate.rate)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getChangeIcon(rate.change)}
                            <Typography
                              variant="caption"
                              sx={{ color: getChangeColor(rate.change), fontWeight: 500 }}
                            >
                              {formatChange(rate.change)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Full Rates Table */}
          <Grid item xs={12}>
            <Card
              elevation={4}
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  All Exchange Rates (Base: {baseCurrency})
                </Typography>
                
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 600, mt: 2 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>24h Change</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rates.map((rate) => (
                          <TableRow
                            key={rate.code}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography component="span">
                                  {rate.flag}
                                </Typography>
                                <Typography variant="body2">
                                  {rate.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {rate.code}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {formatRate(rate.rate)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                {getChangeIcon(rate.change)}
                                <Typography
                                  variant="body2"
                                  sx={{ color: getChangeColor(rate.change), fontWeight: 500 }}
                                >
                                  {formatChange(rate.change)}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default RatesPage;
